import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  LinkIcon, 
  EyeIcon, 
  TrashIcon,
  ClipboardDocumentIcon,
  DocumentArrowUpIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { staffApi } from '../staffApi';

export default function FileShare() {
  const [fileShares, setFileShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchFileShares();
  }, [currentPage]);

  const fetchFileShares = async () => {
    try {
      setLoading(true);
      const response = await staffApi.getFileShares(currentPage, pageSize);
      if (response.success) {
        setFileShares(response.data.results);
        setTotalPages(response.data.pagination.total_pages);
        setTotalCount(response.data.pagination.total_count);
      } else {
        setError(response.message || 'Failed to fetch file shares');
      }
    } catch (error) {
      console.error('Error fetching file shares:', error);
      setError('Failed to fetch file shares');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFileShare = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await staffApi.createFileShare(createForm);
      if (response.success) {
        setCreateForm({ title: '', description: '' });
        setShowCreateModal(false);
        setSelectedShare(response.data);
        setShowLinkModal(true);
        fetchFileShares(); // Refresh the list
      } else {
        setError(response.message || 'Failed to create file share');
      }
    } catch (error) {
      console.error('Error creating file share:', error);
      setError('Failed to create file share');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateFileShare = async (shareId) => {
    if (!window.confirm('Are you sure you want to deactivate this file share?')) {
      return;
    }

    try {
      const response = await staffApi.deactivateFileShare(shareId);
      if (response.success) {
        fetchFileShares(); // Refresh the list
      } else {
        setError(response.message || 'Failed to deactivate file share');
      }
    } catch (error) {
      console.error('Error deactivating file share:', error);
      setError('Failed to deactivate file share');
    }
  };

  const handleViewLink = (share) => {
    setSelectedShare(share);
    setShowLinkModal(true);
  };

  const handleCopyLink = async () => {
    if (selectedShare?.share_token) {
      const link = `${window.location.origin}/file-share/${selectedShare.share_token}`;
      try {
        await navigator.clipboard.writeText(link);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  const getFileShareLink = () => {
    if (selectedShare?.share_token) {
      return `${window.location.origin}/file-share/${selectedShare.share_token}`;
    }
    return '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-sm font-medium rounded-md ${
            i === currentPage
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {pages}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File Sharing</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage file sharing links for students and tutors.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Create New Link
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* File Shares List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading file shares...</p>
          </div>
        ) : fileShares.length === 0 ? (
          <div className="text-center py-12">
            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No file shares</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new file share link.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {fileShares.map((share) => (
              <li key={share.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <LinkIcon className="flex-shrink-0 h-5 w-5 text-gray-400" />
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-indigo-600 truncate">
                            {share.title || 'Untitled File Share'}
                          </p>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            share.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {share.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {share.description && (
                          <p className="text-sm text-gray-500 mt-1">{share.description}</p>
                        )}
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <CalendarDaysIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                          <span>Created {formatDate(share.created_at)} by {share.created_by_name}</span>
                          <span className="mx-2">•</span>
                          <span>{share.files?.length || 0} files</span>
                          <span className="mx-2">•</span>
                          <span>{share.access_count} accesses</span>
                        </div>
                        {share.demo_info && (
                          <div className="mt-2 text-xs text-blue-600">
                            Demo: {share.demo_info.student_name} with {share.demo_info.tutor_name} - {share.demo_info.subject}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewLink(share)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View Link
                    </button>
                    {share.is_active && (
                      <button
                        onClick={() => handleDeactivateFileShare(share.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {!loading && fileShares.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          {renderPagination()}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create File Share Link</h3>
              <form onSubmit={handleCreateFileShare}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title (Optional)
                  </label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter a title for this file share"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter a description for this file share"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && selectedShare && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">File Share Link</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share this link:
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={getFileShareLink()}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {linkCopied ? (
                      <span className="text-green-400">Copied!</span>
                    ) : (
                      <ClipboardDocumentIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Title:</strong> {selectedShare.title || 'Untitled'}<br />
                  <strong>Created:</strong> {formatDate(selectedShare.created_at)}<br />
                  <strong>Status:</strong> {selectedShare.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
